import React, { useState, useEffect } from 'react';
import { RateLimitBlock } from './RateLimitBlock';

/**
 * Componente global que bloqueia toda a aplicação quando há rate limit
 * Deve ser usado no nível mais alto da aplicação (App.js)
 */
export const GlobalRateLimitBlock = () => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [waitTime, setWaitTime] = useState(0);

  useEffect(() => {
    // Verificar bloqueio persistido ao carregar
    const checkPersistedBlock = () => {
      const persisted = localStorage.getItem('rateLimitBlock');
      if (persisted) {
        try {
          const { blockedUntil } = JSON.parse(persisted);
          const now = Date.now();
          const timeRemaining = Math.ceil((blockedUntil - now) / 1000);
          
          if (timeRemaining > 0) {
            setIsBlocked(true);
            setWaitTime(timeRemaining);
            return true;
          } else {
            localStorage.removeItem('rateLimitBlock');
            return false;
          }
        } catch (error) {
          console.error('Erro ao verificar bloqueio persistido:', error);
          localStorage.removeItem('rateLimitBlock');
          return false;
        }
      }
      return false;
    };

    // Verificar imediatamente ao montar
    checkPersistedBlock();
    
    // Listener para eventos de rate limit - SEMPRE adicionar
    const handleRateLimit = (event) => {
      const { waitTime: eventWaitTime, blockFullScreen = true } = event.detail || {};

      if (blockFullScreen === false) {
        return;
      }

      // Garantir que o bloqueio está salvo no localStorage PRIMEIRO
      const blockedUntil = Date.now() + (eventWaitTime * 1000);
      localStorage.setItem('rateLimitBlock', JSON.stringify({
        blockedUntil,
        waitTime: eventWaitTime
      }));
      
      // Atualizar estado IMEDIATAMENTE (forçar re-render)
      setIsBlocked(true);
      setWaitTime(eventWaitTime);
    };

    // Adicionar listener com capture para garantir que seja capturado primeiro
    window.addEventListener('rateLimitExceeded', handleRateLimit, true);
    
    // Também verificar periodicamente se há bloqueio persistido (para sincronizar entre abas)
    const syncInterval = setInterval(() => {
      const persisted = localStorage.getItem('rateLimitBlock');
      if (persisted) {
        try {
          const { blockedUntil } = JSON.parse(persisted);
          const now = Date.now();
          const timeRemaining = Math.ceil((blockedUntil - now) / 1000);
          
          if (timeRemaining <= 0) {
            // Tempo expirado - limpar bloqueio
            localStorage.removeItem('rateLimitBlock');
            setIsBlocked(false);
            setWaitTime(0);
          } else if (timeRemaining > 0) {
            // Atualizar estado se há bloqueio válido
            setIsBlocked(true);
            setWaitTime(timeRemaining);
          }
        } catch (error) {
          console.error('Erro ao sincronizar bloqueio:', error);
          localStorage.removeItem('rateLimitBlock');
          setIsBlocked(false);
        }
      } else {
        // Se não há bloqueio persistido, garantir que estado está desbloqueado
        if (isBlocked) {
          setIsBlocked(false);
          setWaitTime(0);
        }
      }
    }, 1000); // Verificar a cada 1 segundo
    
    return () => {
      window.removeEventListener('rateLimitExceeded', handleRateLimit, true);
      clearInterval(syncInterval);
    };
  }, []); // Manter array vazio - não queremos re-executar quando isBlocked muda

  // Bloquear navegação e interações quando há bloqueio ativo
  useEffect(() => {
    // Só adicionar listeners quando realmente estiver bloqueado
    if (!isBlocked) {
      return; // Não adicionar nenhum listener se não estiver bloqueado
    }

    // Prevenir navegação usando beforeunload
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'O sistema está bloqueado devido ao limite de requisições. Aguarde o tempo restante.';
      return e.returnValue;
    };

    // NÃO bloquear cliques - o bloqueio visual já é suficiente
    // Remover completamente o listener de click para não interferir com React Router
    // const handleClick = ... removido

    // Bloquear todas as interações do teclado quando bloqueado
    const handleKeyDown = (e) => {
      // Permitir apenas F5 (refresh) e Ctrl+R
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        return;
      }
      // Bloquear outras teclas
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    // Remover listeners de click e keydown para não interferir com React Router
    // O bloqueio visual já é suficiente para impedir navegação
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isBlocked]);

  const handleUnblock = () => {
    // Verificar se o bloqueio realmente expirou
    const persisted = localStorage.getItem('rateLimitBlock');
    if (!persisted) {
      // Limpar estado quando não há mais bloqueio persistido
      setIsBlocked(false);
      setWaitTime(0);
    }
  };

  // Só renderizar RateLimitBlock se realmente estiver bloqueado E waitTime > 0
  // Isso evita renderizações desnecessárias que podem interferir com React Router
  if (!isBlocked || !waitTime || waitTime <= 0) {
    return null;
  }

  return (
    <RateLimitBlock 
      isBlocked={isBlocked} 
      waitTime={waitTime}
      onUnblock={handleUnblock}
    />
  );
};

